import { ORG_DOMAINS } from './config';
import type { CurrentEmail, EmailDirection, EmailParticipant } from './types';

// Read the currently-open Outlook message via Office.js. Runs entirely
// client-side in the taskpane - no Microsoft Graph call, no mailbox sync. This
// is the whole point of Option B: the user explicitly opens an email and logs
// THAT email, nothing more.

const getBodyText = (item: Office.MessageRead): Promise<string> =>
  new Promise((resolve, reject) => {
    item.body.getAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(result.error);
      }
    });
  });

// Raw internet headers (for In-Reply-To / References threading). Needs Mailbox
// requirement set 1.8+; absent on some older clients - we degrade gracefully to
// no threading (a new thread per email) rather than failing the log.
const getInternetHeaders = (item: Office.MessageRead): Promise<string> =>
  new Promise((resolve) => {
    const fn = (item as unknown as {
      getAllInternetHeadersAsync?: (
        cb: (result: Office.AsyncResult<string>) => void,
      ) => void;
    }).getAllInternetHeadersAsync;
    if (typeof fn !== 'function') {
      resolve('');
      return;
    }
    try {
      fn.call(item, (result) => {
        resolve(
          result.status === Office.AsyncResultStatus.Succeeded
            ? (result.value ?? '')
            : '',
        );
      });
    } catch {
      resolve('');
    }
  });

// Unfold folded header lines (continuations start with whitespace) then read a
// single header value by name.
const headerValue = (headers: string, name: string): string => {
  const unfolded = headers.replace(/\r?\n[ \t]+/g, ' ');
  const match = unfolded.match(new RegExp(`^${name}:\\s*(.*)$`, 'im'));
  return match?.[1]?.trim() ?? '';
};

const extractMessageIds = (value: string): string[] => value.match(/<[^>]+>/g) ?? [];

export const readCurrentEmail = async (): Promise<CurrentEmail> => {
  const item = Office.context?.mailbox?.item as Office.MessageRead | undefined;
  if (!item) {
    throw new Error('No message is open. Open an email and try again.');
  }

  const body = await getBodyText(item);
  const headers = await getInternetHeaders(item);
  const userEmail = Office.context.mailbox.userProfile.emailAddress;
  const fromEmail = item.from?.emailAddress ?? '';

  // We only sync from the user's own perspective; direction is derived by
  // comparing the sender to the signed-in mailbox owner.
  const direction: EmailDirection =
    fromEmail.toLowerCase() === userEmail.toLowerCase() ? 'OUTBOUND' : 'INBOUND';

  const toRecipients = item.to ?? [];
  const ccRecipients = item.cc ?? [];
  const participants: EmailParticipant[] = [
    ...(fromEmail
      ? [{ email: fromEmail, name: item.from?.displayName ?? '', role: 'FROM' as const }]
      : []),
    ...toRecipients.map((r) => ({
      email: r.emailAddress,
      name: r.displayName ?? '',
      role: 'TO' as const,
    })),
    ...ccRecipients.map((r) => ({
      email: r.emailAddress,
      name: r.displayName ?? '',
      role: 'CC' as const,
    })),
  ].filter((p) => p.email);

  return {
    subject: item.subject ?? '(no subject)',
    fromEmail,
    fromName: item.from?.displayName ?? '',
    toEmails: toRecipients.map((recipient) => recipient.emailAddress),
    sentAt: item.dateTimeCreated
      ? new Date(item.dateTimeCreated).toISOString()
      : null,
    internetMessageId: item.internetMessageId ?? '',
    body,
    direction,
    mailboxOwner: userEmail,
    participants,
    inReplyTo: extractMessageIds(headerValue(headers, 'In-Reply-To'))[0] ?? '',
    references: extractMessageIds(headerValue(headers, 'References')),
    conversationId: item.conversationId ?? '',
  };
};

// The "other party" we link the log to: for an inbound email that's the
// sender; for an outbound one it's the first recipient. Domain drives the
// company match.
export const counterpartyEmail = (email: CurrentEmail): string | null => {
  if (email.direction === 'INBOUND') {
    return email.fromEmail || null;
  }
  return email.toEmails[0] ?? null;
};

export const domainOf = (emailAddress: string): string | null => {
  const at = emailAddress.lastIndexOf('@');
  if (at === -1) return null;
  return emailAddress.slice(at + 1).toLowerCase() || null;
};

// --- Platform / no-reply sender handling -------------------------------------
// Transactional senders (invoicing, e-sign, newsletters) put a no-reply address
// in the From header while the real contact is in the signature. We detect that
// and pull the real contact (email/phone/website) out of the body.

const NO_REPLY_LOCALPART =
  /^(no[._-]?reply|do[._-]?not[._-]?reply|donotreply|notifications?|notify|mailer|mail|automated|auto|bounce|postmaster|system|remail)$/i;

export const isNoReply = (address: string): boolean => {
  const local = address.split('@')[0]?.toLowerCase() ?? '';
  return (
    NO_REPLY_LOCALPART.test(local) ||
    /(^|[._-])(noreply|no-reply|donotreply)([._-]|$)/i.test(local)
  );
};

const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const URL_RE = /\b(?:https?:\/\/|www\.)[a-z0-9.-]+\.[a-z]{2,}\b/gi;
const PHONE_RE = /\+?\d[\d\s().-]{7,}\d/g;

const FREEMAIL = new Set([
  'gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'icloud.com',
  'me.com', 'aol.com', 'protonmail.com', 'proton.me', 'live.com', 'msn.com',
]);

const unique = (values: string[]): string[] => Array.from(new Set(values));

const extractEmails = (body: string): string[] =>
  unique((body.match(EMAIL_RE) ?? []).map((value) => value.toLowerCase()));

const extractWebsiteDomains = (body: string): string[] =>
  unique(
    (body.match(URL_RE) ?? []).map((url) =>
      url
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/\/.*$/, '')
        .toLowerCase(),
    ),
  );

const extractPhones = (body: string): string[] =>
  unique(
    (body.match(PHONE_RE) ?? [])
      .map((value) => value.trim())
      .filter((value) => value.replace(/\D/g, '').length >= 9),
  );

export type ContactGuess = {
  email: string; // effective contact email (header, or a signature address)
  phone: string; // a phone parsed from the signature (for new-record prefill)
  domain: string; // effective company domain
  automatedSender: boolean; // the From header is a no-reply/automated address
  fromSignature: boolean; // we replaced the header with a signature address
};

// Resolve the "real" counterparty: normally the header, but for no-reply senders
// we look in the signature for a business email tied to a website link.
export const resolveContact = (email: CurrentEmail): ContactGuess => {
  const header = counterpartyEmail(email) ?? '';
  const headerDomain = header ? (domainOf(header) ?? '') : '';
  const automatedSender = !!header && isNoReply(header);

  if (!automatedSender) {
    return { email: header, phone: '', domain: headerDomain, automatedSender: false, fromSignature: false };
  }

  const sites = extractWebsiteDomains(email.body);
  const candidates = extractEmails(email.body).filter((address) => {
    const domain = domainOf(address) ?? '';
    return (
      address !== header &&
      !isNoReply(address) &&
      domain !== headerDomain &&
      !ORG_DOMAINS.includes(domain) &&
      !FREEMAIL.has(domain)
    );
  });
  const preferred =
    candidates.find((address) => sites.includes(domainOf(address) ?? '')) ??
    candidates[0];
  const phone = extractPhones(email.body)[0] ?? '';

  if (preferred) {
    return {
      email: preferred,
      phone,
      domain: domainOf(preferred) ?? '',
      automatedSender: true,
      fromSignature: true,
    };
  }
  // automated sender but no better contact found - keep the header, but flag it
  return { email: header, phone, domain: headerDomain, automatedSender: true, fromSignature: false };
};
